import { Component } from 'react'

/**
 * Top-level error boundary. Without this, any uncaught render error
 * unmounts the whole React tree and the user sees a blank white page with
 * no way forward. Here we catch it, show a calm recoverable fallback, and
 * forward the error to whatever reporter is wired up (see reportError).
 *
 * Placement: wraps the entire app in main.jsx, inside <BrowserRouter> so the
 * "Reload" affordance and any future router-aware fallback keep working.
 *
 * React only invokes error boundaries for errors thrown during render,
 * lifecycle methods, and constructors of the tree below — NOT for errors in
 * event handlers or async callbacks (those should be handled where they
 * occur, e.g. react-query onError + ErrorBanner, which the app already does).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // Forward to the app's error reporter if one is installed (e.g. Sentry,
    // wired in src/observability). Falls back to console so nothing is lost
    // during local dev. Kept defensive so the boundary itself never throws.
    try {
      if (typeof window !== 'undefined' && window.__icerReportError) {
        window.__icerReportError(error, { componentStack: info?.componentStack })
      } else {
        console.error('Unhandled UI error:', error, info?.componentStack)
      }
    } catch {
      /* reporter failed — never let the boundary crash */
    }
  }

  handleReload = () => {
    // Full reload is the safest recovery for an unknown render fault — it
    // re-runs bootstrap (token restore, /me/ fetch) from a clean slate.
    if (typeof window !== 'undefined') window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-wantok-50 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                 stroke="#0f766e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-slate-800">Something went wrong</h1>
          <p className="mt-2 text-sm text-slate-600">
            The page hit an unexpected error. Your data is safe — reloading
            usually fixes it. If it keeps happening, please contact support.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              onClick={this.handleReload}
              className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-4 py-2"
            >
              Reload
            </button>
            <a
              href="/"
              className="text-sm text-wantok-700 hover:text-wantok-800 underline px-2 py-2"
            >
              Go to home
            </a>
          </div>
        </div>
      </div>
    )
  }
}
